import React, { useState, useRef } from 'react';
import { useT } from '../i18n';

interface PairData {
  pairId: number;
  textA: string;
  textB: string;
  genre: string | null;
  humanSide: 'A' | 'B';
  round: number;
}

interface ArenaProps {
  pair: PairData;
  round: number;
  totalRounds: number;
  onVote: (chosenSide: 'A' | 'B', timeSpentMs: number) => void;
  loading: boolean;
}

export default function Arena({ pair, round, totalRounds, onVote, loading }: ArenaProps) {
  const [selected, setSelected] = useState<'A' | 'B' | null>(null);
  const startTime = useRef(Date.now());
  const t = useT();

  const handleVote = (side: 'A' | 'B') => {
    if (loading || selected) return;
    setSelected(side);
    const timeSpent = Date.now() - startTime.current;
    onVote(side, timeSpent);
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 py-8">
      {/* Progress */}
      <div className="w-full max-w-4xl mb-8">
        <div className="flex items-center justify-between mb-3">
          <span className="text-stone-400 text-sm font-sans">
            {t('arena.round')} {round} {t('arena.of')} {totalRounds}
          </span>
          {pair.genre && (
            <span className="text-xs font-sans px-3 py-1 bg-stone-100 border border-stone-200 rounded-full text-stone-500">
              {pair.genre}
            </span>
          )}
        </div>
        <div className="w-full bg-stone-200 rounded-full h-1">
          <div
            className="bg-stone-400 h-1 rounded-full transition-all duration-500"
            style={{ width: `${((round - 1) / totalRounds) * 100}%` }}
          />
        </div>
      </div>

      <p className="font-display text-xl text-stone-700 mb-6 text-center italic">
        {t('arena.question')}
      </p>

      {/* Text Cards */}
      <div className="w-full max-w-4xl grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        {(['A', 'B'] as const).map((side) => (
          <button
            key={side}
            onClick={() => handleVote(side)}
            disabled={loading || selected !== null}
            className={`relative text-left p-6 md:p-8 rounded-xl border transition-all duration-200 ${
              selected === side
                ? 'border-stone-400 bg-stone-100'
                : 'border-stone-200 bg-white hover:border-stone-400 hover:shadow-md'
            } ${loading || selected ? 'cursor-default' : 'cursor-pointer'}`}
          >
            <span className="absolute top-3 left-5 text-[11px] font-sans uppercase tracking-widest text-stone-300">
              Text {side}
            </span>
            <p className="mt-4 font-serif text-stone-700 leading-[1.8] whitespace-pre-line">
              {side === 'A' ? pair.textA : pair.textB}
            </p>
          </button>
        ))}
      </div>
    </div>
  );
}
